import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CloudArrowUp, FileText, DownloadSimple, Trash, FilePdf, FileImage, FileCsv, FileDoc, FileXls, Eye } from "@phosphor-icons/react";
import AttachmentPreviewDialog from "@/components/AttachmentPreviewDialog";

const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

const iconFor = (ct, name) => {
  const n = (name || "").toLowerCase();
  if (ct?.includes("pdf") || n.endsWith(".pdf")) return FilePdf;
  if (ct?.startsWith("image/")) return FileImage;
  if (ct?.includes("csv") || n.endsWith(".csv")) return FileCsv;
  if (n.endsWith(".docx") || n.endsWith(".doc")) return FileDoc;
  if (n.endsWith(".xlsx") || n.endsWith(".xls")) return FileXls;
  return FileText;
};

export default function DocumentsPanel({ clientId, engagementId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Iter 85.57 · Shared preview dialog for the same canonical download URL.
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    const params = new URLSearchParams();
    if (clientId) params.set("client_id", clientId);
    if (engagementId) params.set("engagement_id", engagementId);
    api.get(`/documents?${params}`).then((r) => setDocs(r.data));
  };
  useEffect(() => { load(); }, [clientId, engagementId]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const params = new URLSearchParams();
        if (clientId) params.set("client_id", clientId);
        if (engagementId) params.set("engagement_id", engagementId);
        await api.post(`/documents/upload?${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (d) => {
    // Auth cookie is sent automatically; fetch → blob so browser applies content-disposition.
    fetch(`${API}/documents/${d.id}/download`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = d.original_filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const del = async (d) => {
    if (!confirm(`Delete "${d.original_filename}"?`)) return;
    await api.delete(`/documents/${d.id}`);
    toast.success("Document removed");
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDrop}
        className="border border-dashed border-border bg-card p-8 text-center cursor-pointer hover:border-primary/60 transition-colors"
        onClick={() => fileRef.current?.click()}
        data-testid="documents-dropzone"
      >
        <CloudArrowUp size={32} className="mx-auto text-primary" weight="duotone" />
        <div className="mt-2 font-display text-base font-bold">
          {uploading ? "Uploading…" : "Drop files or click to upload"}
        </div>
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
          PDF · DOCX · XLSX · PPTX · PNG · JPG · CSV · TXT — up to 25MB
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => handleFiles(Array.from(e.target.files))}
          data-testid="documents-file-input"
        />
      </div>

      <div className="border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h3 className="font-display font-bold text-sm">Attached files ({docs.length})</h3>
        </div>
        {docs.length === 0 ? (
          <div className="px-5 py-8 text-center text-xs text-muted-foreground">No documents yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {docs.map((d) => {
              const Icon = iconFor(d.content_type, d.original_filename);
              const openPreview = () => setPreview({
                url: `${API}/documents/${d.id}/download`,
                filename: d.original_filename,
                contentType: d.content_type,
                size: d.size,
                uploadedBy: d.uploader_name,
              });
              return (
                <div key={d.id} className="px-5 py-3 flex items-center gap-3 hover:bg-secondary/30" data-testid={`document-row-${d.id}`}>
                  <Icon size={22} className="text-primary shrink-0" weight="duotone" />
                  <button
                    type="button"
                    onClick={openPreview}
                    data-testid={`preview-doc-${d.id}`}
                    className="flex-1 min-w-0 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                    aria-label={`Preview ${d.original_filename}`}
                  >
                    <div className="text-sm font-medium truncate hover:text-primary transition-colors">{d.original_filename}</div>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                      {fmtSize(d.size)} · {d.uploader_name || "—"} · {new Date(d.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <button onClick={openPreview} className="text-muted-foreground hover:text-primary p-1" title="Preview" data-testid={`view-doc-${d.id}`}>
                    <Eye size={16} />
                  </button>
                  <button onClick={() => download(d)} className="text-muted-foreground hover:text-primary p-1" title="Download" data-testid={`download-doc-${d.id}`}>
                    <DownloadSimple size={16} />
                  </button>
                  <button onClick={() => del(d)} className="text-muted-foreground hover:text-destructive p-1" title="Delete" data-testid={`delete-doc-${d.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AttachmentPreviewDialog
        open={!!preview}
        onOpenChange={(o) => { if (!o) setPreview(null); }}
        url={preview?.url}
        filename={preview?.filename}
        contentType={preview?.contentType}
        size={preview?.size}
        uploadedBy={preview?.uploadedBy}
      />
    </div>
  );
}
